/**
 * Centralized API client — all backend fetches go through here.
 * Same-origin architecture: CloudFront proxies /api/* to API GW.
 *
 * Uses @amzn/sentry-fetch for transparent Midway authentication:
 * - Automatically handles session expiry via modal popup
 * - No manual auth bootstrap or 401 redirect logic needed
 *
 * Includes:
 * - 403 recovery interceptor (detects team membership revocation)
 */

import { configure } from '@amzn/sentry-fetch';
import { useAuthStore } from '../stores/starnexus/authStore';

// ============================================================================
// URL Builder
// ============================================================================

/** Build an API URL. Returns a relative path (same-origin). */
export function apiUrl(path: string): string {
  return path.startsWith('/api/') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
}

/** Default fetch options. Empty — same-origin sends cookies automatically. */
export const apiFetchDefaults: RequestInit = {};

// ============================================================================
// Sentry-Fetch Configuration
// ============================================================================

/**
 * In DEV mode (local vite dev server), sentry-fetch is not needed — the vite
 * proxy handles API calls directly without Midway. In production, sentry-fetch
 * transparently handles Midway session expiry via a modal popup.
 *
 * Fallback: if @amzn/sentry-fetch is not installed (e.g. WIRE unavailable during build),
 * fall back to globalThis.fetch. The app will still work — users just get redirected
 * to Midway login page on session expiry instead of seeing a modal.
 *
 * NOTE: In DEV/test mode we use a getter function (not a captured reference) so that
 * test mocks on globalThis.fetch are picked up at call time.
 */
let sentryFetch: typeof fetch;
if (import.meta.env.DEV) {
  // Use a wrapper that reads globalThis.fetch at call time — allows test mocks to work
  sentryFetch = (...args: Parameters<typeof fetch>) => globalThis.fetch(...args);
} else {
  try {
    sentryFetch = configure({
      followMidwayStepUp: true,
      followMidwayStepUpOptions: { mode: 'modal' },
    });
  } catch {
    sentryFetch = (...args: Parameters<typeof fetch>) => globalThis.fetch(...args);
  }
}

// ============================================================================
// 403 Recovery Interceptor
// ============================================================================

/**
 * Checks whether a 403 response indicates a team membership issue
 * (user was removed from team or team was deleted).
 */
function is403MembershipError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('not a member') || lower.includes('not authorized');
}

/**
 * Handles 403 recovery by reloading teams from the backend.
 * After reload:
 * - If teams is empty → AuthGuard will automatically show OnboardingPage
 * - If active team is gone → reloadTeams() already falls back to first team
 *   and clears localStorage
 */
async function handle403Recovery(): Promise<void> {
  await useAuthStore.getState().reloadTeams();
}

// ============================================================================
// Primary Fetch Wrapper
// ============================================================================

/**
 * Fetch wrapper with 403 recovery interceptor.
 * Uses sentry-fetch for transparent Midway authentication (session expiry
 * triggers a modal popup automatically — no manual redirect needed).
 *
 * Detects 403 responses with membership-related error messages and triggers
 * a teams reload to keep the auth store in sync with backend state.
 *
 * The original response is still returned to the caller so they can handle
 * the error in their own UI (e.g., show a flash message).
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await sentryFetch(input, { ...apiFetchDefaults, ...init });

  // Fallback auth redirect: if sentry-fetch is not active and the response
  // indicates a Midway auth failure (401 from midway-auth redirect, or the
  // response URL contains midway-auth), redirect the whole page to /sso/login.
  if (response.status === 401 && response.url?.includes('midway-auth.amazon.com')) {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/sso/login?redirect_uri=${encodeURIComponent(window.location.origin + currentPath)}`;
    // Return the response anyway (page will navigate away)
    return response;
  }

  if (response.status === 403) {
    try {
      const cloned = response.clone();
      const body = await cloned.json();
      if (body.message && is403MembershipError(body.message)) {
        await handle403Recovery();
      }
    } catch {
      // If we can't parse the body, skip recovery
    }
  }

  return response;
}
